import React, { Component } from 'react'
import debounce from 'lodash/debounce'

import Scroller from 'client/shared/blocks/scroller'
import { Loader } from 'client/shared/blocks/loader'
import Spacer from 'client/shared/blocks/spacer'

const SCROLL_RATIO = 0.85

type Props = {
  page: number
  load: (page: number) => void
  updatePage?: (page: number) => void
  pagesAmount?: number
  pagination?: boolean
  elements?: number
  reset?: boolean
}

type State = {
  pagesMarks: Record<string, any>
  loading: boolean
  currentPage: number
  loadedPages: number
  scrollHeight: number
}

export default class InfiniteScroll extends Component<Props, State> {
  static defaultProps = {
    pagination: false,
  }

  static getDerivedStateFromProps(props) {
    if (props.elements <= 20) {
      // update state params then user click on page in pagination block and see "new" page
      return {
        currentPage: 1,
        loadedPages: 1,
      }
    }
    return null
  }

  state = {
    loading: false,
    pagesMarks: {},
    currentPage: 1,
    loadedPages: 1,
    scrollHeight: 0,
  }

  _load = async (): Promise<void> => {
    const { loading, loadedPages, currentPage, pagesMarks } = this.state
    const { load, page, pagesAmount } = this.props

    if (page === pagesAmount || !page || loading) return

    if (currentPage + 1 <= loadedPages) {
      // prevent dounloading exists pages
      this.changePageUrl(page + 1)
      this.changeCurrentPage(currentPage + 1)
      return
    }

    this.setState({ loading: true })

    try {
      if (window.pageYOffset < pagesMarks[currentPage] - window.innerHeight) {
        this.hideLoader()
        return // fix for prevent jumping on next page
      }
      await load(page + 1)
      this.hideLoader()
      this.updateLoadedPages()
      this.setState(state => ({
        pagesMarks: {
          ...state.pagesMarks,
          [currentPage + 1]: this.state.scrollHeight * SCROLL_RATIO,
        },
      }))
      this.changePageUrl(page + 1)
      this.changeCurrentPage(currentPage + 1)
    } catch {
      this.hideLoader()
    }
  }

  load = debounce(this._load, 500)

  checkScroll = (value: number): void => {
    const { pagesMarks, currentPage } = this.state
    if (pagesMarks[currentPage] - window.innerHeight < value) {
      this.load()
    }
  }

  checkScrollUp = (value: number): void => {
    const { pagesMarks, currentPage } = this.state

    if (value < pagesMarks[currentPage - 1] - window.innerHeight) {
      this.changePageUrl(this.props.page - 1)
      this.changeCurrentPage(currentPage - 1)
    }
  }

  changeCurrentPage = (number: number): void => {
    this.setState({ currentPage: number })
  }

  changePageUrl = (page: number): void => {
    const { updatePage } = this.props
    if (updatePage) {
      updatePage(page)
    }
  }

  getScrollHeight(): number {
    return window.document.body.scrollHeight
  }

  setScrollHeight(): void {
    this.setState({ scrollHeight: this.getScrollHeight() })
  }

  componentDidMount(): void {
    this.setScrollHeight()
    this.checkScroll(window.pageYOffset)
    this.setState({ pagesMarks: { 1: this.state.scrollHeight * SCROLL_RATIO } })
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.scrollHeight !== this.getScrollHeight()) {
      this.setScrollHeight()
    }

    if (prevProps.reset !== this.props.reset) {
      this.setState({ currentPage: 1, loadedPages: 1 })
    }
  }

  hideLoader(): void {
    this.setState({ loading: false })
  }

  updateLoadedPages(): void {
    const { loadedPages } = this.state
    this.setState({ loadedPages: loadedPages + 1 })
  }

  render(): JSX.Element {
    const { loading } = this.state
    const { children } = this.props

    return (
      <Scroller
        onScroll={{ down: this.checkScroll, up: this.checkScrollUp }}
        permanently
      >
        <div>
          {children}
          {loading && (
            <div>
              <Spacer />
              <Loader color="gray" kind="big" centered />
            </div>
          )}
        </div>
      </Scroller>
    )
  }
}
